import React from "react";
import classnames from "classnames";
import { ProgressBar } from "./ProgressBar";
import styles from "./AttachmentTile.module.scss";

export type AttachmentState = "uploading" | "done" | "error";

export interface AttachmentTileProps {
    /** Имя файла */
    name: string;
    /** Размер в байтах; не задан — не показывается */
    size?: number;
    /** MIME-тип; вместе с именем определяет метку типа, если нет эскиза */
    mime?: string;
    /** URL эскиза картинки */
    preview?: string;
    /** Состояние: загружается, готово, ошибка (по умолчанию «готово») */
    state?: AttachmentState;
    /** Ход загрузки, 0–1; показывается в состоянии «uploading» */
    progress?: number;
    /** «Повторить»: показывается только в состоянии «error» */
    onRetry?: () => void;
    /** «Убрать»: показывается в любом состоянии, если передан */
    onRemove?: () => void;
    className?: string;
}

/** Размер файла для людей: «350 КБ», «1,2 МБ» (десятичная запятая) */
export const formatFileSize = (bytes: number): string => {
    const fmt = (value: number) => (Math.round(value * 10) / 10).toString().replace(".", ",");
    if (!Number.isFinite(bytes) || bytes < 0) return "";
    if (bytes < 1024) return `${Math.round(bytes)} Б`;
    if (bytes < 1024 * 1024) return `${fmt(bytes / 1024)} КБ`;
    if (bytes < 1024 * 1024 * 1024) return `${fmt(bytes / 1024 / 1024)} МБ`;
    return `${fmt(bytes / 1024 / 1024 / 1024)} ГБ`;
};

/** Текстовая метка типа файла: расширение из имени, иначе подтип из MIME */
const typeLabel = (name: string, mime?: string): string => {
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 && dot < name.length - 1 ? name.slice(dot + 1) : (mime?.split("/")[1] ?? "");
    return ext.slice(0, 4).toUpperCase() || "ФАЙЛ";
};

/**
 * Плитка вложения: эскиз или метка типа файла, имя, размер, состояние загрузки. При ошибке —
 * «Ошибка загрузки» и кнопки «Повторить»/«Убрать». Кнопки имеют зону касания не меньше 44×44.
 */
export const AttachmentTile = ({
    name,
    size,
    mime,
    preview,
    state = "done",
    progress = 0,
    onRetry,
    onRemove,
    className,
}: AttachmentTileProps) => (
    <div className={classnames(styles.tile, { [styles.error]: state === "error" }, className)}>
        <div className={styles.thumb} aria-hidden="true">
            {preview ? <img className={styles.image} src={preview} alt="" /> : typeLabel(name, mime)}
        </div>
        <div className={styles.info}>
            <span className={styles.name}>{name}</span>
            {size !== undefined && <span className={styles.meta}>{formatFileSize(size)}</span>}
            {state === "uploading" && <ProgressBar value={progress} label={`Загрузка: ${name}`} />}
            {state === "error" && (
                <span className={styles.errorText} role="alert">
                    Ошибка загрузки
                </span>
            )}
        </div>
        <div className={styles.actions}>
            {state === "error" && onRetry && (
                <button type="button" className={styles.action} onClick={onRetry}>
                    Повторить
                </button>
            )}
            {onRemove && (
                <button type="button" className={styles.action} onClick={onRemove}>
                    Убрать
                </button>
            )}
        </div>
    </div>
);
